import api from './api';

/**
 * Load carousel from backend.
 * Returns the raw carousel object including the slides[] array
 * with base_image_url, title, description, slide_data per slide.
 * @param {string} carouselId - UUID of the carousel
 * @returns {Promise<object>} - Raw carousel data from backend
 */
export const loadCarousel = async (carouselId) => {
  try {
    console.log('� Loading carousel:', carouselId);
    const response = await api.get(`/carousels/${carouselId}/`);
    const data = response.data;
    console.log(`✅ Loaded: "${data.title}" — ${data.slides?.length || 0} slide(s)`);
    return data;
  } catch (error) {
    console.error('❌ Load error:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Save carousel to backend.
 * Step 1: PATCH carousel canvas_data (dimensions, background, etc.)
 * Step 2: For each editor slide, PATCH the existing backend slide's slide_data
 *         or POST a new slide if it doesn't exist yet.
 *
 * ⚠️  BACKEND REQUIRED: PATCH /api/carousels/{id}/slides/{slideId}/
 *     See EDITOR_OPTION_A.md for details.
 *
 * @param {string} carouselId - UUID of the carousel
 * @param {object} canvasData - Editor canvas state { version, editor, slides, dimensions, background }
 * @param {Array}  backendSlides - Raw slides array from backend (each has .id and .order)
 * @returns {Promise<object>} - Updated carousel data from backend
 */
export const saveCarousel = async (carouselId, canvasData, backendSlides = []) => {
  try {
    console.log('� Step 1: Saving carousel settings...');
    await api.patch(`/carousels/${carouselId}/`, {
      canvas_data: {
        version: canvasData.version,
        editor: canvasData.editor,
        dimensions: canvasData.dimensions,
        background: canvasData.background,
      },
    });

    console.log('💾 Step 2: Saving slides...');
    const slides = canvasData.slides || [];

    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      const backendSlide = backendSlides.find(s => s.order === i);

      const slidePayload = {
        slide_data: {
          version: '1.0',
          name: slide.name || `Slide ${i + 1}`,
          elements: slide.elements || [],
        },
        order: i,
      };

      if (backendSlide?.id) {
        console.log(`  Updating slide ${i + 1} (backend id: ${backendSlide.id})`);
        await api.patch(`/carousels/${carouselId}/slides/${backendSlide.id}/`, slidePayload);
      } else {
        console.log(`  Creating slide ${i + 1}`);
        await api.post(`/carousels/${carouselId}/slides/`, slidePayload);
      }
    }

    console.log('📥 Fetching updated carousel...');
    const finalResponse = await api.get(`/carousels/${carouselId}/`);
    console.log('✅ Save complete');
    return finalResponse.data;
  } catch (error) {
    console.error('❌ Save error:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Create a new carousel (used by Option B standalone editor).
 * @param {object} carouselData - { title, platform }
 * @returns {Promise<object>} - Created carousel data (includes id)
 */
export const createCarousel = async (carouselData) => {
  try {
    const response = await api.post('/carousels/', carouselData);
    return response.data;
  } catch (error) {
    console.error('❌ Create carousel error:', error.response?.data || error.message);
    throw error;
  }
};
